import React, { useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { Mail, MessageSquare, Route, Search, Shield, Sparkles, TrendingUp, Upload, UserPlus } from "lucide-react";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import { useOnScreen } from "../../hooks/useOnScreen";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import { AnimatedCounter } from "../../components/diy/AnimatedCounter";
import { ScoreGauge } from "../../components/diy/ScoreGauge";
import { FaqItem } from "../../components/diy/FaqAccordion";
import "../../styles/diy-animations.css";

const faqItems: { question: string; answer: string }[] = [
  {
    question: "Is it legal to dispute items on my credit report?",
    answer:
      "Absolutely. The Fair Credit Reporting Act (FCRA) gives you the legal right to dispute any inaccurate, incomplete, or unverifiable information on your credit report. This is a federally protected consumer right.",
  },
  {
    question: "How long does credit repair take?",
    answer:
      "Results vary by individual. Credit bureaus have 30 days to investigate disputes. Some members see deletions in 30-45 days, while more complex cases may take several rounds of disputes over a few months.",
  },
  {
    question: "What kind of items can I dispute?",
    answer:
      "Collections, late payments, charge-offs, bankruptcies, inquiries, and any item that contains inaccurate information. The AI analyzes each item and identifies the strongest dispute angles.",
  },
  {
    question: "Do I need to know anything about credit repair?",
    answer:
      "Not at all. The guided wizard walks you through every step, and the AI Credit Coach is available 24/7 to answer your questions. We built this for beginners.",
  },
  {
    question: "What if it doesn't work?",
    answer:
      "We offer a 7-day money-back guarantee. If you're not satisfied for any reason within the first 7 days, you get a full refund. No questions asked.",
  },
  {
    question: "How is this different from free dispute templates?",
    answer:
      "Free templates are generic. IntelliWarrior's AI reads YOUR specific credit report and generates personalized dispute letters based on the exact errors found. Every letter is unique to your situation.",
  },
  {
    question: "Do you guarantee a specific credit score increase?",
    answer:
      "No — and any company that does is violating federal law (CROA). What we guarantee is that you'll have the best tools and guidance to dispute inaccurate items effectively.",
  },
];

const features: {
  title: string;
  desc: string;
  iconBg: string;
  iconColor: string;
  Icon: LucideIcon;
}[] = [
  {
    title: "AI Credit Report Analysis",
    desc: "Upload your report and get instant AI analysis of every negative item.",
    iconBg: "rgba(14,165,233,0.12)",
    iconColor: "#0EA5E9",
    Icon: Sparkles,
  },
  {
    title: "Unlimited Dispute Letters",
    desc: "Generate as many dispute letters as you need, tailored to each item.",
    iconBg: "rgba(16,185,129,0.12)",
    iconColor: "#10B981",
    Icon: Mail,
  },
  {
    title: "Credit Coach Chatbot",
    desc: "Ask questions anytime and get personalized guidance on your credit journey.",
    iconBg: "rgba(139,92,246,0.12)",
    iconColor: "#8B5CF6",
    Icon: MessageSquare,
  },
  {
    title: "Guided Wizard",
    desc: "Never wonder what to do next — the dashboard walks you through everything.",
    iconBg: "rgba(245,158,11,0.12)",
    iconColor: "#F59E0B",
    Icon: Route,
  },
  {
    title: "Progress Tracking",
    desc: "Watch your disputes move through the pipeline from sent to resolved.",
    iconBg: "rgba(236,72,153,0.12)",
    iconColor: "#EC4899",
    Icon: TrendingUp,
  },
  {
    title: "Bank-Level Security",
    desc: "Bank-level encryption keeps your data protected.",
    iconBg: "rgba(100,116,139,0.12)",
    iconColor: "#64748B",
    Icon: Shield,
  },
];

const steps: { title: string; desc: string; color: string; Icon: LucideIcon }[] = [
  {
    title: "Sign up",
    desc: "Account in under 2 minutes. No credit check.",
    color: "#0EA5E9",
    Icon: UserPlus,
  },
  {
    title: "Upload your report",
    desc: "Grab your free report from AnnualCreditReport.com.",
    color: "#10B981",
    Icon: Upload,
  },
  {
    title: "Review AI findings",
    desc: "AI flags errors, inaccuracies, and disputable items.",
    color: "#8B5CF6",
    Icon: Search,
  },
  {
    title: "Send your letters",
    desc: "Print, sign, and mail — we guide you through every step.",
    color: "#F59E0B",
    Icon: Mail,
  },
];

function HeroProgressBar() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useOnScreen(ref, 0.12);
  return (
    <div ref={ref} className="relative mt-6 w-full">
      <div className="relative h-2 w-full overflow-visible rounded-full bg-white/5">
        <div
          className="h-2 rounded-full"
          style={{
            background: "linear-gradient(90deg, #EF4444, #F59E0B, #10B981, #0EA5E9)",
            width: visible ? "100%" : "0%",
            transition: "width 2.5s ease-out",
          }}
        />
        <div
          className="absolute top-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
          style={{ left: "35%", background: "#F59E0B" }}
        />
        <div
          className="absolute top-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
          style={{ left: "65%", background: "#10B981" }}
        />
      </div>
    </div>
  );
}

const DiyLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkout = searchParams.get("checkout");
  const scrollY = useScrollPosition();
  const navSolid = scrollY > 50;
  const showSticky = scrollY >= 600;

  const goRegister = () => navigate("/diy/register");
  const goLogin = () => navigate("/login");

  return (
    <div
      className="diy-landing min-h-screen overflow-x-hidden"
      style={{ backgroundColor: "#0B1120", fontFamily: "'DM Sans', sans-serif" }}
    >
      <nav
        className="fixed left-0 right-0 top-0 z-50 px-6 py-4"
        style={{
          background: navSolid ? "rgba(11,17,32,0.95)" : "transparent",
          backdropFilter: navSolid ? "blur(24px)" : "none",
          WebkitBackdropFilter: navSolid ? "blur(24px)" : "none",
          borderBottom: navSolid ? "1px solid rgba(255,255,255,0.06)" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "linear-gradient(135deg, #10B981, #0EA5E9)" }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 3L5 6v5c0 4.5 3.2 8.7 7 10 3.8-1.3 7-5.5 7-10V6l-7-3z"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span
              style={{
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: "#F1F5F9",
                letterSpacing: "-0.5px",
              }}
            >
              IntelliWarrior
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goLogin}
              className="cursor-pointer border-0 bg-transparent"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14, color: "#94A3B8" }}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={goRegister}
              className="cursor-pointer rounded-xl border-0 text-white"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                padding: "10px 20px",
                background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20 shrink-0" aria-hidden />

      {checkout === "cancel" && (
        <div
          className="border-b px-4 py-3 text-center text-sm"
          style={{ borderColor: "rgba(245,158,11,0.35)", background: "rgba(120,53,15,0.25)", color: "#FDE68A" }}
        >
          Checkout was canceled. You can try again anytime from your account.
        </div>
      )}
      {checkout === "required" && (
        <div
          className="border-b px-4 py-3 text-center text-sm"
          style={{ borderColor: "rgba(14,165,233,0.35)", background: "rgba(12,74,110,0.35)", color: "#E0F2FE" }}
        >
          Complete your subscription to access the DIY dashboard. Use the link from registration email or register
          again to open checkout.
        </div>
      )}

      <section className="relative overflow-hidden px-6 pb-20 pt-8 md:pt-12">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.12) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 20% 80%, rgba(16,185,129,0.08) 0%, transparent 50%), radial-gradient(ellipse 40% 40% at 80% 60%, rgba(139,92,246,0.06) 0%, transparent 50%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-80"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative z-10 mx-auto max-w-lg text-center md:text-left">
          <div
            className="animate-fade-down mx-auto mb-5 inline-flex items-center gap-2 rounded-full py-2 pl-4 pr-4 md:mx-0"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}
          >
            <span
              className="animate-pulse-dot h-1.5 w-1.5 rounded-full"
              style={{ background: "#10B981", boxShadow: "0 0 8px rgba(16,185,129,0.6)" }}
            />
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 11,
                color: "#10B981",
                letterSpacing: 2,
              }}
            >
              DIY CREDIT REPAIR
            </span>
          </div>

          <h1 className="animate-fade-up-1 mb-5" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 42, lineHeight: 1.08 }}>
            <span className="block" style={{ color: "#F1F5F9" }}>
              Fix your credit
            </span>
            <span className="block">
              <span className="gradient-text-green-blue">yourself.</span>
              <span style={{ color: "#94A3B8", fontWeight: 400 }}> AI</span>
            </span>
            <span className="block" style={{ color: "#94A3B8", fontWeight: 400 }}>
              makes it easy.
            </span>
          </h1>

          <p
            className="animate-fade-up-2 mb-9"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: 16, color: "#94A3B8", lineHeight: 1.6 }}
          >
            Upload your report. Our AI finds the errors, drafts the dispute letters, and walks you through every step.
          </p>

          <div className="animate-fade-up-3 glass-card-subtle card-hover relative mx-auto max-w-md rounded-2xl p-6 md:mx-0">
            <div className="absolute right-5 top-5 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#10B981" }} />
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 9,
                  color: "#94A3B8",
                  letterSpacing: 2,
                }}
              >
                LIVE TRACKING
              </span>
            </div>
            <div className="mt-6 flex flex-row items-center justify-around gap-2">
              <div className="flex flex-col items-center">
                <span
                  className="mb-2 block"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#EF4444",
                    letterSpacing: 2,
                  }}
                >
                  BEFORE
                </span>
                <ScoreGauge score={520} label="POOR" color="#EF4444" glowColor="rgba(239,68,68,0.3)" />
              </div>
              <div className="flex flex-col items-center px-1 pt-6">
                <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#94A3B8" }}>+230</span>
                <svg width="40" height="20" viewBox="0 0 40 20" className="mt-1" aria-hidden>
                  <defs>
                    <linearGradient id="arrowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#EF4444" />
                      <stop offset="100%" stopColor="#10B981" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M4 10h28m0 0l-6-6m6 6l-6 6"
                    stroke="url(#arrowGrad)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <div className="flex flex-col items-center">
                <span
                  className="mb-2 block"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#10B981",
                    letterSpacing: 2,
                  }}
                >
                  GOAL
                </span>
                <ScoreGauge score={750} label="EXCELLENT" color="#10B981" glowColor="rgba(16,185,129,0.3)" animated />
              </div>
            </div>
            <HeroProgressBar />
          </div>

          <div className="animate-fade-up-4 mx-auto mt-8 text-center md:mx-0 md:text-left">
            <div className="items-baseline justify-center gap-2 md:flex md:justify-start">
              <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 36, color: "#F1F5F9" }}>$49.99</span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: 16, color: "#64748B" }}>/ month</span>
            </div>
            <p className="mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14 }}>
              <span style={{ color: "#94A3B8" }}>Cancel anytime · </span>
              <span style={{ color: "#10B981" }}>7-day money-back guarantee</span>
            </p>
          </div>

          <button
            type="button"
            onClick={goRegister}
            className="animate-fade-up-5 animate-cta-glow mx-auto mt-6 w-full max-w-md cursor-pointer border-0 text-center transition-transform hover:scale-[1.02] md:mx-0"
            style={{
              fontFamily: "'Sora', sans-serif",
              fontWeight: 700,
              fontSize: 18,
              color: "white",
              padding: "20px 24px",
              borderRadius: 16,
              background: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
              boxShadow: "0 8px 30px rgba(16,185,129,0.35), 0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            Start fixing my credit →
          </button>
          <p className="mt-3 text-center md:text-left" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#64748B" }}>
            No credit check · Bank-level security
          </p>
        </div>
      </section>

      <section className="relative px-6 py-20">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: "radial-gradient(ellipse 80% 50% at 50% 50%, rgba(16,185,129,0.03) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-10">
          <ScrollReveal>
            <p
              className="mb-3 text-center"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 11,
                color: "#10B981",
                letterSpacing: 3,
              }}
            >
              WHAT&apos;S INSIDE
            </p>
            <h2 className="text-center" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 30, color: "#F1F5F9" }}>
              Everything you need.
            </h2>
            <h2
              className="gradient-text-green-blue text-center italic"
              style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 30 }}
            >
              Nothing you don&apos;t.
            </h2>
            <p className="mb-10 mt-2 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#64748B" }}>
              Six tools. One simple workflow.
            </p>
          </ScrollReveal>

          <div className="mx-auto grid max-w-lg grid-cols-2 gap-3.5">
            {features.map((f, i) => (
              <ScrollReveal key={f.title} delay={i * 0.08}>
                <div className="glass-card-subtle card-hover h-full rounded-2xl p-5">
                  <div
                    className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl"
                    style={{ background: f.iconBg }}
                  >
                    <f.Icon className="h-[22px] w-[22px]" strokeWidth={2} color={f.iconColor} aria-hidden />
                  </div>
                  <h3 className="mb-1" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 14, color: "#E2E8F0" }}>
                    {f.title}
                  </h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: 12.5, color: "#94A3B8", lineHeight: 1.5 }}>
                    {f.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20">
        <ScrollReveal>
          <h2 className="text-center" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 32, color: "#F1F5F9" }}>
            Four steps.
          </h2>
          <h2
            className="gradient-text-green-blue text-center italic"
            style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 32 }}
          >
            Real results.
          </h2>
          <p className="mb-12 mt-2 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#64748B" }}>
            From signup to your first dispute in under an hour.
          </p>
        </ScrollReveal>

        <div className="mx-auto max-w-lg">
          {steps.map((step, index) => {
            const next = steps[index + 1];
            const lineGradient = next
              ? `linear-gradient(to bottom, ${step.color}4D, ${next.color}26)`
              : undefined;
            return (
              <ScrollReveal key={step.title} delay={index * 0.12}>
                <div className="relative mb-10 flex gap-5">
                  <div className="flex shrink-0 flex-col items-center">
                    <div className="relative">
                      <div
                        className="flex h-14 w-14 items-center justify-center rounded-2xl"
                        style={{
                          border: `2px solid ${step.color}4D`,
                          background: `${step.color}14`,
                        }}
                      >
                        <step.Icon className="h-6 w-6" strokeWidth={2} color={step.color} aria-hidden />
                      </div>
                      <div
                        className="absolute -right-1 -top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 text-white"
                        style={{
                          background: step.color,
                          borderColor: "#0B1120",
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 700,
                          fontSize: 10,
                        }}
                      >
                        {index + 1}
                      </div>
                      {next && (
                        <div className="mt-2 h-10 w-0.5 shrink-0 rounded-full" style={{ background: lineGradient }} />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="mb-1" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 18, color: "#F1F5F9" }}>
                      {step.title}
                    </h3>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: 14, color: "#94A3B8", lineHeight: 1.6 }}>
                      {step.desc}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </section>

      <section className="px-6 py-20">
        <ScrollReveal>
          <p
            className="mb-3 text-center"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              fontSize: 11,
              color: "#8B5CF6",
              letterSpacing: 3,
            }}
          >
            THE MATH
          </p>
          <h2 className="text-center" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 28, color: "#F1F5F9" }}>
            Save up to{" "}
            <AnimatedCounter
              end={1200}
              duration={1800}
              prefix="$"
              suffix="/year."
              className="gradient-text-green-blue"
            />
          </h2>
          <p className="mb-8 mt-2 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#64748B" }}>
            vs. hiring a credit repair company
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <div
            className="mx-auto max-w-lg overflow-hidden rounded-2xl"
            style={{ border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <div
              className="grid grid-cols-3"
              style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div className="px-4 py-3.5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 10, color: "#64748B", letterSpacing: 1.5 }}>
                FEATURE
              </div>
              <div className="px-4 py-3.5 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 12, color: "#64748B" }}>
                Repair Co.
              </div>
              <div className="px-4 py-3.5 text-center" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 12, color: "#10B981" }}>
                IntelliWarrior
              </div>
            </div>

            {[
              {
                feature: "Monthly cost",
                company: <span style={{ color: "#EF4444" }}>$79–$149</span>,
                diy: (
                  <span style={{ color: "#10B981", fontWeight: 700 }}>
                    $49.99
                  </span>
                ),
              },
              {
                feature: "Contract",
                company: <span style={{ color: "#EF4444" }}>6–12 months</span>,
                diy: (
                  <span style={{ color: "#10B981", fontWeight: 700 }}>
                    None
                  </span>
                ),
              },
              {
                feature: "You're in control",
                company: <div className="mx-auto h-2.5 w-2.5 rounded-full" style={{ background: "rgba(239,68,68,0.3)" }} />,
                diy: (
                  <div
                    className="mx-auto h-2.5 w-2.5 rounded-full"
                    style={{ background: "#10B981", boxShadow: "0 0 10px rgba(16,185,129,0.6)" }}
                  />
                ),
              },
              {
                feature: "AI-powered",
                company: <div className="mx-auto h-2.5 w-2.5 rounded-full" style={{ background: "rgba(239,68,68,0.3)" }} />,
                diy: (
                  <div
                    className="mx-auto h-2.5 w-2.5 rounded-full"
                    style={{ background: "#10B981", boxShadow: "0 0 10px rgba(16,185,129,0.6)" }}
                  />
                ),
              },
              {
                feature: "Cancel anytime",
                company: <span style={{ color: "#EF4444" }}>Fees apply</span>,
                diy: (
                  <span style={{ color: "#10B981", fontWeight: 700 }}>
                    Free
                  </span>
                ),
              },
            ].map((row, ri, arr) => (
              <div
                key={row.feature}
                className="grid grid-cols-3"
                style={{
                  background: ri % 2 === 1 ? "rgba(255,255,255,0.015)" : "transparent",
                  borderBottom: ri < arr.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                }}
              >
                <div className="px-4 py-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 13, color: "#CBD5E1" }}>
                  {row.feature}
                </div>
                <div
                  className="flex items-center justify-center px-4 py-4 text-center"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 13, color: "#CBD5E1" }}
                >
                  {row.company}
                </div>
                <div
                  className="flex items-center justify-center px-4 py-4 text-center"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 13, color: "#CBD5E1" }}
                >
                  {row.diy}
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </section>

      <section className="px-6 py-20">
        <ScrollReveal>
          <h2 className="mb-8 text-center" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 28, color: "#F1F5F9" }}>
            Got questions?
          </h2>
        </ScrollReveal>
        <div className="mx-auto max-w-xl">
          {faqItems.map((item, i) => (
            <ScrollReveal key={item.question} delay={i * 0.05}>
              <FaqItem question={item.question} answer={item.answer} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="px-6 py-20 pb-32">
        <ScrollReveal direction="scale">
          <div className="glass-card relative mx-auto max-w-lg overflow-hidden rounded-3xl p-10 text-center">
            <div
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                background: "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(16,185,129,0.08) 0%, transparent 60%)",
              }}
            />
            <div className="relative z-10">
              <div
                className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full py-1.5 pl-4 pr-4"
                style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)" }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#10B981" }} />
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 11,
                    color: "#10B981",
                    letterSpacing: 2,
                  }}
                >
                  LIMITED TIME
                </span>
              </div>
              <h2 className="mb-3" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 28, color: "#F1F5F9" }}>
                Your credit story starts today.
              </h2>
              <p className="mb-8" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: 14, color: "#94A3B8" }}>
                Join thousands of warriors who took back control of their credit.
              </p>
              <button
                type="button"
                onClick={goRegister}
                className="animate-cta-glow w-full cursor-pointer border-0 transition-transform hover:scale-[1.02]"
                style={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  color: "white",
                  padding: "20px 24px",
                  borderRadius: 16,
                  background: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
                  boxShadow: "0 8px 30px rgba(16,185,129,0.35), 0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                Get started — $49.99/mo →
              </button>
              <p className="mt-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#64748B" }}>
                7-day money-back guarantee · Cancel anytime · No credit check
              </p>
            </div>
          </div>
        </ScrollReveal>
      </section>

      <footer
        className="border-t px-6 pb-28 pt-10 text-center"
        style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(11,17,32,0.6)" }}
      >
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#94A3B8" }}>© 2026 Warrior Financial Services LLC</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          <Link to="/privacy" className="text-slate-500 hover:text-emerald-400">
            Privacy Policy
          </Link>
          <span className="text-slate-700">|</span>
          <Link to="/terms" className="text-slate-500 hover:text-emerald-400">
            Terms of Service
          </Link>
          <span className="text-slate-700">|</span>
          <a href="mailto:info@intelliwarrior.com" className="text-slate-500 hover:text-emerald-400">
            Contact
          </a>
        </div>
        <p className="mt-3">
          <a href="mailto:info@intelliwarrior.com" className="text-slate-500 hover:text-emerald-400" style={{ fontSize: 14 }}>
            info@intelliwarrior.com
          </a>
        </p>
      </footer>

      <div
        className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3"
        style={{
          background: "rgba(11,17,32,0.95)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          opacity: showSticky ? 1 : 0,
          transform: showSticky ? "translateY(0)" : "translateY(100%)",
          pointerEvents: showSticky ? "auto" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <div>
          <div className="flex items-baseline gap-1">
            <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 18, color: "white" }}>$49.99</span>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: 13, color: "#64748B" }}>/mo</span>
          </div>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#10B981" }}>7-day guarantee</p>
        </div>
        <button
          type="button"
          onClick={goRegister}
          className="cursor-pointer rounded-xl border-0 text-white"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            fontSize: 14,
            padding: "12px 28px",
            background: "linear-gradient(135deg, #10B981, #0EA5E9)",
            boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
          }}
        >
          Get Started →
        </button>
      </div>
    </div>
  );
};

export default DiyLandingPage;
